/**
 * Account Creation Screen
 * 
 * Full account creation flow for web browser (not MFE).
 * Collects email, password, and consents, then creates GCIP account.
 * 
 * @module components/auth/AccountCreationScreen
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from '@sequelae/lore-core';
import { validate as emailValidator } from 'email-validator';
import { useAuth } from '@/contexts/AuthContext';
import { AccountCreationApiService } from '@/services/api';
import { readDeviceInformation } from '@/utils/deviceFingerprint';
import { getSignUpPasswordError } from '@/utils/passwordValidation';
import './AccountCreationScreen.css';

const EMAIL_EXISTS_ERROR_MESSAGE =
  'This email is already associated with a Lore account. Please sign in.';

export const AccountCreationScreen: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  // Session state
  const [loadingSession, setLoadingSession] = useState<boolean>(true);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [sessionError, setSessionError] = useState<string>('');

  // Form state
  const [inputEmail, setInputEmail] = useState<string>('');
  const [inputPassword, setInputPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [inputEmailValidationError, setInputEmailValidationError] = useState<string>('');
  const [inputPasswordValidationError, setInputPasswordValidationError] = useState<string>('');
  const [isCheckingEmail, setIsCheckingEmail] = useState<boolean>(false);

  // Consent checkboxes
  const [inputBoxAge, setInputBoxAge] = useState<boolean>(false);
  const [inputBoxTerms, setInputBoxTerms] = useState<boolean>(false);
  const [inputBoxNotDoc, setInputBoxNotDoc] = useState<boolean>(false);

  // Submission state
  const [allFieldsValid, setAllFieldsValid] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');

  // Modal state
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalUrl, setModalUrl] = useState<string>('');
  const [modalTitle, setModalTitle] = useState<string>('');

  // Email check refs
  const emailCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  const emailCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Modal handlers
  const openTermsModal = () => {
    setModalTitle('Terms of Use');
    setModalUrl('https://www.lore.co/terms');
    setShowModal(true);
  };

  const openPrivacyModal = () => {
    setModalTitle('Privacy Policy');
    setModalUrl('https://www.lore.co/privacypolicy');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalUrl('');
    setModalTitle('');
  };

  /**
   * Validate email format
   */
  const validateEmail = useCallback((email: string): string => {
    if (!email) {
      return 'Email is required.';
    }
    if (!emailValidator(email)) {
      return 'Please enter a valid email address.';
    }
    return '';
  }, []);

  /**
   * Validate password
   */
  const validatePassword = useCallback((password: string): string => {
    return getSignUpPasswordError(password);
  }, []);

  /**
   * Check if email exists via API
   */
  const checkEmailExists = useCallback(async (email: string) => {
    try {
      const result = await AccountCreationApiService.checkEmailExists(email);
      return result;
    } catch (error) {
      console.warn('[AccountCreation] Email check error:', error);
      return null;
    }
  }, []);

  /**
   * Handle email input change with debounced API check
   */
  const handleEmailChange = useCallback(
    (value: string) => {
      setInputEmail(value);
      const error = validateEmail(value);
      setInputEmailValidationError(error);

      // Clear any existing timers
      if (emailCheckTimerRef.current) {
        clearTimeout(emailCheckTimerRef.current);
      }
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }

      // If email is valid format, check if it exists after a delay
      if (!error && value) {
        setIsCheckingEmail(true);
        emailCheckTimerRef.current = setTimeout(async () => {
          const result = await checkEmailExists(value);

          // Safety timeout
          emailCheckTimeoutRef.current = setTimeout(() => {
            setIsCheckingEmail(false);
          }, 6000);

          setIsCheckingEmail(false);
          if (emailCheckTimeoutRef.current) {
            clearTimeout(emailCheckTimeoutRef.current);
          }

          if (result?.exists) {
            setInputEmailValidationError(EMAIL_EXISTS_ERROR_MESSAGE);
          }
        }, 500);
      } else {
        setIsCheckingEmail(false);
      }
    },
    [validateEmail, checkEmailExists]
  );

  /**
   * Handle password input change
   */
  const handlePasswordChange = (value: string) => {
    setInputPassword(value);
    const error = validatePassword(value);
    setInputPasswordValidationError(error);
  };

  /**
   * Update allFieldsValid whenever form state changes
   */
  useEffect(() => {
    const isEmailValid = inputEmail && !inputEmailValidationError && !isCheckingEmail;
    const isPasswordValid = inputPassword && !inputPasswordValidationError;
    const areCheckboxesValid = inputBoxAge && inputBoxTerms && inputBoxNotDoc;

    setAllFieldsValid(!!(isEmailValid && isPasswordValid && areCheckboxesValid));
  }, [
    inputEmail,
    inputEmailValidationError,
    inputPassword,
    inputPasswordValidationError,
    inputBoxAge,
    inputBoxTerms,
    inputBoxNotDoc,
    isCheckingEmail,
  ]);

  /**
   * Cleanup timers on unmount
   */
  useEffect(() => {
    return () => {
      if (emailCheckTimerRef.current) {
        clearTimeout(emailCheckTimerRef.current);
      }
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Initialize session on mount
   */
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const initializeSession = async () => {
      setLoadingSession(true);
      setSessionError('');

      try {
        console.log('[AccountCreation] Initializing session...');
        const deviceInfo = readDeviceInformation();
        const response = await AccountCreationApiService.createSession({
          device_info: JSON.stringify(deviceInfo),
          visit_source: 1,
          coupon_code: null,
        });

        if (response.oe_user_session_id) {
          console.log('[AccountCreation] Session created:', response.oe_user_session_id);
          setSessionId(response.oe_user_session_id);
        } else {
          console.warn('[AccountCreation] No session ID in response');
          setSessionError('Failed to initialize session');
        }
      } catch (error) {
        console.error('[AccountCreation] Session error:', error);
        setSessionError('Failed to connect to server. Please try again.');
      } finally {
        setLoadingSession(false);
      }
    };

    initializeSession();
  }, []);

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    navigate('/auth');
  };

  /**
   * Handle form submission - create account
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!allFieldsValid) {
      console.warn('[AccountCreation] Form validation failed');
      return;
    }

    if (!sessionId) {
      setSubmitError('Session not initialized. Please refresh the page.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      console.log('[AccountCreation] Creating account...');

      const response = await AccountCreationApiService.createAccount({
        email: inputEmail,
        password: inputPassword,
        session_id: sessionId,
        auth_provider: 'gcip',
      });

      console.log('[AccountCreation] Account created successfully');

      await loginWithToken(response.access_token);
      window.location.href = '/rewards';
      
    } catch (error: any) {
      console.error('[AccountCreation] Error:', error);
      
      let errorMessage = 'Failed to create account. Please try again.';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (data.error_message) {
          errorMessage = data.error_message;
        } else if (data.detail) {
          errorMessage = typeof data.detail === 'string' 
            ? data.detail 
            : Array.isArray(data.detail) 
              ? data.detail.map((err: any) => err.msg).join(', ')
              : 'An error occurred.';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading session
  if (loadingSession) {
    return (
      <div className="account-creation-container">
        <div className="account-creation-loading">
          <Spinner />
          <p className="loading-text">Initializing...</p>
        </div>
      </div>
    );
  }

  // Session error
  if (sessionError) {
    return (
      <div className="account-creation-container">
        <div className="account-creation-error">
          <h2 className="error-title">Connection Error</h2>
          <p className="error-description">{sessionError}</p>
          <Button
            variant="primary"
            size="medium"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="account-creation-container">
      <div className="account-creation-content">
        <header className="account-creation-header">
          <h1 className="page-title">Create Your Lore Account</h1>
          <button 
            className="close-btn" 
            onClick={handleBack}
            aria-label="Close"
            disabled={isSubmitting}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <form onSubmit={handleSubmit} className="account-creation-form">
          {/* Email Field */}
          <div className="form-field">
            <label htmlFor="email">Email Address</label>
            <div className="input-wrapper">
              <input
                id="email"
                type="email"
                value={inputEmail}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="Enter your email"
                disabled={isSubmitting}
                className={inputEmailValidationError ? 'input-error' : ''}
                autoComplete="email"
              />
              {isCheckingEmail && (
                <div className="input-spinner">
                  <Spinner />
                </div>
              )}
            </div>
            {inputEmailValidationError && (
              <p className="field-error">{inputEmailValidationError}</p>
            )}
          </div>

          {/* Password Field */}
          <div className="form-field">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={inputPassword}
                onChange={(e) => handlePasswordChange(e.target.value)}
                placeholder="At least 12 characters, mix of types"
                disabled={isSubmitting}
                className={inputPasswordValidationError ? 'input-error' : ''}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {inputPasswordValidationError && (
              <p className="field-error">{inputPasswordValidationError}</p>
            )}
          </div>

          {/* Consent Checkboxes */}
          <div className="checkbox-container">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={inputBoxAge}
                onChange={(e) => setInputBoxAge(e.target.checked)}
                disabled={isSubmitting}
              />
              <span>I am 18 years or older</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={inputBoxTerms}
                onChange={(e) => setInputBoxTerms(e.target.checked)}
                disabled={isSubmitting}
              />
              <span>
                I agree to the{' '}
                <button type="button" className="link-button" onClick={openTermsModal}>
                  Terms of Use
                </button>{' '}
                and{' '}
                <button type="button" className="link-button" onClick={openPrivacyModal}>
                  Privacy Policy
                </button>
              </span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={inputBoxNotDoc}
                onChange={(e) => setInputBoxNotDoc(e.target.checked)}
                disabled={isSubmitting}
              />
              <span>
                I understand Lore is not a substitute for professional medical advice and I should
                consult my doctor for medical decisions
              </span>
            </label>
          </div>

          {/* Submit Error */}
          {submitError && (
            <div className="submit-error">
              <p>{submitError}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={!allFieldsValid || isSubmitting}
          >
            {isSubmitting ? <Spinner /> : 'Create Account'}
          </Button>
        </form>
      </div>

      {/* Terms/Privacy Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modalTitle}</h2>
              <button className="modal-close" onClick={closeModal} aria-label="Close modal">
                ✕
              </button>
            </div>
            <div className="modal-body">
              <iframe src={modalUrl} title={modalTitle} className="modal-iframe" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
